import React, { useState, useEffect, useMemo } from 'react';
import { Link as LinkIcon, FileText, ExternalLink } from 'lucide-react';
import type { LinkSettingsProps } from './types';

type LinkMode = 'page' | 'external' | 'none';

/**
 * Detect the current link mode based on the URL value
 */
const detectLinkMode = (value: string | undefined, pages: { slug: string }[]): LinkMode => {
  if (!value) return 'none';

  // Check if it matches a page (home or specific page)
  if (value === '/' || value === '#') {
    return 'page';
  }

  // Check if value looks like a relative page path
  const looksLikePagePath =
    value.startsWith('/') && !value.startsWith('//') && !value.includes('://');

  const matchingPage = pages.find(page => value === `/${page.slug}` || value === page.slug);
  if (matchingPage || looksLikePagePath) {
    return 'page';
  }

  // Default to external for URLs
  return 'external';
};

export const LinkSettings: React.FC<LinkSettingsProps> = ({
  logoData,
  onChange,
  pages = [],
  isLoadingPages = false,
}) => {
  // Detect initial mode based on existing linkUrl
  const initialMode = useMemo(
    () => detectLinkMode(logoData.linkUrl, pages),
    // Only compute on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [mode, setMode] = useState<LinkMode>(initialMode);

  // Update mode when linkUrl changes externally
  useEffect(() => {
    const detectedMode = detectLinkMode(logoData.linkUrl, pages);
    setMode(detectedMode);
  }, [logoData.linkUrl, pages]);

  // Check if current value matches a page
  const matchingPage = pages.find(
    page => logoData.linkUrl === `/${page.slug}` || logoData.linkUrl === page.slug
  );

  // Handle mode change
  const handleModeChange = (newMode: LinkMode) => {
    setMode(newMode);
    if (newMode === 'page') {
      // Default to home page
      onChange({ linkUrl: '/', linkTarget: '_self' });
    } else if (newMode === 'external') {
      onChange({ linkUrl: '', linkTarget: '_blank' });
    } else {
      // No link
      onChange({ linkUrl: undefined, linkTarget: '_self' });
    }
  };

  // Handle page selection
  const handlePageChange = (value: string) => {
    onChange({ linkUrl: value || '/' });
  };

  // Handle external URL change
  const handleExternalUrlChange = (value: string) => {
    onChange({ linkUrl: value || undefined });
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
      <h3 className="font-medium text-gray-900 flex items-center space-x-2">
        <LinkIcon size={18} />
        <span>Link Settings (Optional)</span>
      </h3>

      {/* Mode Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Logo Click Action</label>
        <div className="flex rounded-lg border border-gray-300 p-0.5 bg-gray-100">
          <button
            type="button"
            onClick={() => handleModeChange('none')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'none'
                ? 'bg-white text-blue-700 shadow-sm border border-blue-300'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span>No Link</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('page')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'page'
                ? 'bg-white text-blue-700 shadow-sm border border-blue-300'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FileText size={14} />
            <span>Page</span>
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('external')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              mode === 'external'
                ? 'bg-white text-blue-700 shadow-sm border border-blue-300'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ExternalLink size={14} />
            <span>External</span>
          </button>
        </div>
      </div>

      {/* Page Selector */}
      {mode === 'page' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Page</label>
          <select
            value={
              matchingPage
                ? `/${matchingPage.slug}`
                : logoData.linkUrl === '/' || logoData.linkUrl === '#'
                  ? logoData.linkUrl
                  : '/'
            }
            onChange={e => handlePageChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            disabled={isLoadingPages}
          >
            <option value="/">{isLoadingPages ? 'Loading pages...' : 'Home'}</option>
            {pages.map(page => (
              <option key={page.id} value={`/${page.slug}`}>
                {page.title} (/{page.slug})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Clicking the logo will navigate to this page.
          </p>
        </div>
      )}

      {/* External URL Input */}
      {mode === 'external' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">External URL</label>
          <input
            type="url"
            value={logoData.linkUrl || ''}
            onChange={e => handleExternalUrlChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://example.com"
          />
          <p className="mt-1 text-xs text-gray-500">
            Enter a full URL (https://...) for external links.
          </p>
        </div>
      )}

      {/* Open in setting - only show when there's a link */}
      {mode !== 'none' && logoData.linkUrl && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Open in</label>
          <select
            value={logoData.linkTarget || '_self'}
            onChange={e => onChange({ linkTarget: e.target.value as '_self' | '_blank' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="_self">Same tab</option>
            <option value="_blank">New tab</option>
          </select>
        </div>
      )}
    </div>
  );
};
